import "./styles.scss";

import { useFluidProperty } from "hooks/useFluidProperty";
import {
  Background,
  Container,
  Content,
  ResponsiveImage,
  Section,
} from "layout";
import { useAppLoad, useViewport } from "providers/AppProvider";
import { type JSX, memo, useEffect, useRef } from "react";
import {
  AnimatedText,
  FadeIn,
  OverlayFade,
  Separator,
  Wallpaper,
} from "ui/index";
import { animateFloat } from "utils/animation";

import shipImage1x from "./images/ship@1x.webp";
import shipImage2x from "./images/ship@2x.webp";

function About(): JSX.Element {
  const { appIsLoaded } = useAppLoad();
  const { assetSize, breakpoints } = useViewport();
  const refShip = useRef<HTMLImageElement>(null);

  const fluidAmplitudeX = useFluidProperty({
    minWidth: breakpoints.md,
    maxWidth: breakpoints.xl,
    minValue: 5,
    maxValue: 10,
  });

  const fluidAmplitudeY = useFluidProperty({
    minWidth: breakpoints.md,
    maxWidth: breakpoints.xl,
    minValue: 20,
    maxValue: 30,
  });

  useEffect(() => {
    if (!appIsLoaded || assetSize === "mobile") return;
    return animateFloat({
      amplitudeX: fluidAmplitudeX,
      amplitudeY: fluidAmplitudeY,
      ref: refShip,
      rotationRange: 180,
      tickTime: 0.0075,
    });
  }, [appIsLoaded, assetSize, fluidAmplitudeX, fluidAmplitudeY]);

  return (
    <Section className="about">
      <Container className="about__container">
        <Background>
          <Wallpaper />
          <OverlayFade opacity={0.8} />
        </Background>
        <Content className="about__content">
          {appIsLoaded && (
            <>
              <ResponsiveImage
                alt="Ship"
                className="about__image"
                fallbackSrc={shipImage1x}
                height={544}
                hideOnMobile={true}
                lazy={false}
                ref={refShip}
                sizes="(max-width: 768px) 248px, 496px"
                srcSet={`${shipImage1x} 1x, ${shipImage2x} 2x`}
                width={496}
              />
              <div className="about__text">
                <h1 className="heading--2 about__title">
                  <AnimatedText text="Marvel at Feats of Spectacular Ingenuity" />
                </h1>
                <div className="body text-accent about__description">
                  <FadeIn>
                    <span>
                      A seasoned practitioner of the digital arts, I’ve spent
                      over two decades conjuring responsive websites, immersive
                      interfaces, and interactive experiences that delight and
                      perform. With a sharp eye for detail and a touch of the
                      theatrical, I specialize in elegant animations, refined
                      design, and front-end sorcery powered by React, HTML, CSS,
                      and their modern kin.
                    </span>
                  </FadeIn>
                </div>
              </div>
            </>
          )}
        </Content>
      </Container>
      <Separator />
    </Section>
  );
}

export default memo(About);
